import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf, NgClass } from '@angular/common';

type ConfirmVariant = 'primary' | 'warning' | 'danger';

/**
 * Generic, presentational confirmation modal.
 *
 * The parent owns all state — it decides the copy, whether the modal is open,
 * and what happens on confirm/cancel. This keeps the dialog reusable (archive,
 * delete, etc.) without baking in any specific action.
 */
@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  imports: [NgIf, NgClass],
  templateUrl: './confirm-modal.component.html',
  styleUrl: './confirm-modal.component.scss'
})
export class ConfirmModalComponent {
  @Input() isVisible = false;
  @Input() title = 'Are you sure?';
  @Input() message = '';
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';
  @Input() variant: ConfirmVariant = 'primary';
  /** When true the confirm button shows a spinner and both buttons are disabled. */
  @Input() processing = false;

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  get iconClasses(): string {
    switch (this.variant) {
      case 'danger':
        return 'bg-red-100 text-red-600';
      case 'warning':
        return 'bg-amber-100 text-amber-600';
      default:
        return 'bg-blue-600/10 text-blue-600';
    }
  }

  get confirmButtonClasses(): string {
    switch (this.variant) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700';
      case 'warning':
        return 'bg-amber-500 hover:bg-amber-600';
      default:
        return 'bg-blue-600 hover:bg-blue-700';
    }
  }

  onConfirm(): void {
    if (this.processing) {
      return;
    }
    this.confirmed.emit();
  }

  onCancel(): void {
    if (this.processing) {
      return;
    }
    this.cancelled.emit();
  }
}
